import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paymentAPI } from '@/services/api';
import { CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveUrl } from "@/utils/adUtil";

export default function PaymentSuccessPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
	const [txnInfo, setTxnInfo] = useState({});
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('cancelled');
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    if (attempts >= 5) { setStatus('timeout'); return; }
    try {
      const res = await paymentAPI.status(sessionId);
			setTxnInfo({ checkoutType: res.data.checkout_type, adId: res.data.ad_id, adType: res.data.ad_type });
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    } catch {
      setStatus('error');
    }
  };

  const icons = {
    loading: <Clock className="w-16 h-16 text-[#D4C5A9] animate-pulse" />,
    success: <CheckCircle className="w-16 h-16 text-green-600" />,
    cancelled: <XCircle className="w-16 h-16 text-[#666055]" />,
    expired: <XCircle className="w-16 h-16 text-[#9B2C2C]" />,
    error: <XCircle className="w-16 h-16 text-[#9B2C2C]" />,
    timeout: <Clock className="w-16 h-16 text-[#8B4513]" />,
  };

	const getTitle = () => {
		if (status === 'success') {
			if (txnInfo.checkoutType === 'ad_order') return t('payment.ad_live');
			if (txnInfo.checkoutType === 'seller_verification') return t('payment.verification_submitted');
			return t('payment.success');
		}
		if (status === 'cancelled') return t('payment.cancel');
		if (status === 'loading') return t('payment.processing');
		if (status === 'expired') return t('payment.session_expired');
		if (status === 'timeout') return t('payment.verification_timeout');
		return t('payment.failed');
	};

	const getSubtitle = () => {
		if (status === 'success') {
			if (txnInfo.checkoutType === 'ad_order') return t('payment.ad_live_desc');
			return t('payment.upgrade_active');
		}
		return null;
	};

	const adLink = txnInfo.adId && txnInfo.adType
		? `/${ resolveUrl(txnInfo.adType) }/${txnInfo.adId}`
		: null;

  return (
    <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center px-4" data-testid="payment-result-page">
      <div className="text-center max-w-md animate-fade-in-up">
        <div className="mb-6">{icons[status]}</div>
        <h1 className="text-2xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-2">{getTitle()}</h1>
        { getSubtitle() && <p className="text-[#666055] mb-6">{ getSubtitle() }</p> }
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
					{status === 'success' && adLink && (
						<Link to={adLink}>
							<Button
								className={`${status === 'success' && adLink ? 'bg-[#1A3C34]' : 'bg-[#1A3C34]'} hover:bg-[#153029] 
										text-white rounded-sm px-8 h-12 btn-equi`}
								data-testid="go-dashboard-btn">
								{t('payment.view_ad')}
								<ArrowRight className="w-4 h-4 ml-2" />
							</Button>
						</Link>
					)}
          <Link to="/dashboard">
            <Button className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm px-8 h-12 btn-equi">
              {t('nav.dashboard')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/horses">
            <Button variant="outline" className="border-[#E6E2D6] rounded-sm px-8 h-12">{t('nav.horses')}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
